// Adapted from 'Components\Account\Shared\PasskeySubmit.razor.js' in blazor Web App project template
// with 'Server' interactive render mode 
// and 'Individual Accounts' authentication type
((PublicKeyCredential, credentials, customElements) => {
    const browserSupportsPasskeys =
        typeof credentials !== 'undefined' &&
        typeof PublicKeyCredential !== 'undefined' &&
        typeof PublicKeyCredential.parseCreationOptionsFromJSON === 'function' &&
        typeof PublicKeyCredential.parseRequestOptionsFromJSON === 'function';

    async function fetchJson(url: string, options = {}) {
        const response = await fetch(url, { credentials: 'include', headers: { 'Accept': 'application/json' }, ...options });
        if (!response.ok) throw new Error(`The server responded with status ${response.status}.`);
        return await response.json();
    }

    async function createCredential(userId: string | null, resetToken: string | null, headers: HeadersInit, signal: AbortSignal) {
        const json = await fetchJson(`/Passkey/CreationOptions?userId=${userId}&resetToken=${encodeURIComponent(resetToken || '')}`, { method: 'POST', headers, signal });
        const publicKey = PublicKeyCredential.parseCreationOptionsFromJSON(json);
        return await credentials.create({ publicKey, signal }) as PublicKeyCredential;
    }

    async function requestCredential(mediation: CredentialMediationRequirement | undefined, headers: HeadersInit, signal: AbortSignal) {
        const json = await fetchJson('/Passkey/RequestOptions', { method: 'POST', headers, signal });
        const publicKey = PublicKeyCredential.parseRequestOptionsFromJSON(json);
        return await credentials.get({ publicKey, mediation, signal }) as PublicKeyCredential;
    }

    // JSON.stringify() fallback for various password managers
    // https://github.com/dotnet/aspnetcore/pull/62917/commits/0c35fc3a60b2d97e03d03d10713784885bdc0856
    function credentialsToJson(credential: PublicKeyCredential): string {
        try {
            return JSON.stringify(credential);

        } catch (error: any) {
            if (error.name !== 'TypeError') throw error;

            // Some password managers do not implement PublicKeyCredential.prototype.toJSON correctly,
            // which is required for JSON.stringify() to work.
            // e.g. https://www.1password.community/discussions/1password/typeerror-illegal-invocation-in-chrome-browser/47399
            // Try and serialize the credential to JSON manually.
            const { type, authenticatorAttachment, id } = credential;
            const response = credential.response as any;

            return JSON.stringify({
                id,
                type,
                authenticatorAttachment,
                clientExtensionResults: credential.getClientExtensionResults(),
                rawId: toBase64(credential.rawId),
                response: {
                    attestationObject: toBase64(response.attestationObject),
                    authenticatorData: toBase64(response.authenticatorData ?? response.getAuthenticatorData?.() ?? undefined),
                    clientDataJSON: toBase64(response.clientDataJSON),
                    publicKey: toBase64(response.getPublicKey?.() ?? undefined),
                    publicKeyAlgorithm: response.getPublicKeyAlgorithm?.() ?? undefined,
                    transports: response.getTransports?.() ?? undefined,
                    signature: toBase64(response.signature),
                    userHandle: toBase64(response.userHandle),
                },
            })
        }
    }

    function toBase64(o: any) {
        if (!o) return undefined;
        if (Array.isArray(o)) o = Uint8Array.from(o);
        if (o instanceof ArrayBuffer) o = new Uint8Array(o);
        if (o instanceof Uint8Array) o = btoa(o.reduce((s, i) => s + String.fromCharCode(i), ''));
        if (typeof o !== 'string') throw new Error("Could not convert to base64 string");
        return o.replace(/\+/g, "-").replace(/\//g, "_").replace(/=*$/g, ""); // Convert base64 to base64url
    }

    customElements.define('passkey-submit', class extends HTMLElement {
        internals: ElementInternals = null!;

        errorDiv?: HTMLDivElement

        abortController?: AbortController = null!;

        attrs: {
            operation: "Create" | "Request",
            userId: string | null,
            resetToken: string | null,
            requestTokenName: string | null,
            requestTokenValue: string | null
        } = null!;

        static formAssociated = true;

        // Custom element lifecycle callbacks: https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#custom_element_lifecycle_callbacks
        // Called each time the element is added to the document
        connectedCallback() {
            this.internals = this.attachInternals();

            this.attrs = {
                operation: this.getAttribute('operation') as any,
                userId: this.getAttribute('user-id'),
                resetToken: this.getAttribute('reset-token'),
                requestTokenName: this.getAttribute('request-token-name'),
                requestTokenValue: this.getAttribute('request-token-value'),
            };

            this.internals.form!.addEventListener('submit', (event) => {
                if ((event.submitter as HTMLButtonElement)?.name === '__passkeySubmit') {
                    event.preventDefault();
                    this.obtainAndSubmitCredential();
                }
            });

            this.tryAutofillPasskey();
        }

        // Called each time the element is removed from the document
        disconnectedCallback() {
            this.abortController?.abort();
        }

        async obtainCredential(mediation: CredentialMediationRequirement | undefined, signal: AbortSignal) {
            if (!browserSupportsPasskeys) {
                throw new Error('Some passkey features are missing. Please update your browser.');
            }

            const { operation, requestTokenName, requestTokenValue } = this.attrs;

            const headers: HeadersInit = { };

            if (requestTokenValue) {
                headers[requestTokenName ?? "__RequestVerificationToken"] = requestTokenValue
            }

            switch (operation) {
                case "Create": return await createCredential(this.attrs.userId, this.attrs.resetToken, headers, signal);
                case "Request": return await requestCredential(mediation, headers, signal);
                default: throw new Error(`Unknown passkey operation '${operation}'.`);
            }
        }

        async obtainAndSubmitCredential(mediation?: CredentialMediationRequirement) {
            this.errorDiv?.remove();
            this.errorDiv = undefined;

            this.abortController?.abort();
            this.abortController = new AbortController();
            
            try {
                const credential = await this.obtainCredential(mediation, this.abortController.signal)!;
                const credentialJson = credentialsToJson(credential);
                const email = (document.getElementById('Email') as HTMLInputElement)?.value;
                const formData = new FormData();
                formData.append('Email', email);
                formData.append('CredentialJson', credentialJson);

                this.internals.setFormValue(formData);
                this.internals.form!.submit();

            } catch (error: any) {
                if (error.name === 'AbortError') {
                    return; // The user explicitly canceled the operation - return without error.

                } else if (error.name == 'NotAllowedError' && mediation === "conditional") {
                    return;

                } else if (error.name == 'NotAllowedError') {
                    this.showError('The passkey was not provided by the authenticator.');

                } else {
                    console.error(error);
                    this.showError(error.message)
                }
            }
        }

        async tryAutofillPasskey() {
            if (browserSupportsPasskeys && this.attrs.operation === 'Request' && await PublicKeyCredential.isConditionalMediationAvailable?.()) {
                await this.obtainAndSubmitCredential('conditional');
            }
        }

        showError(message: string) {
            if (!this.errorDiv) {
                this.errorDiv = document.createElement('div');
                this.errorDiv.className = 'text-danger small mx-2';
                this.appendChild(this.errorDiv);
            }
            this.errorDiv.innerText = message;
        }
    });

})(window.PublicKeyCredential, window.navigator.credentials, window.customElements);